import React from 'react';
import {
  Fab,
  Tooltip,
  Zoom,
  Typography,
  useTheme,
  useMediaQuery,
  Box,
} from '@mui/material';
import { keyframes } from '@emotion/react';

// Animation keyframes
const pulse = keyframes`
  0%   { transform: scale(1);   box-shadow: 0 4px 20px rgba(0,0,0,0.15); }
  50%  { transform: scale(1.05); box-shadow: 0 6px 30px rgba(0,0,0,0.25); }
  100% { transform: scale(1);   box-shadow: 0 4px 20px rgba(0,0,0,0.15); }
`;

const bounce = keyframes`
  0%, 20%, 53%, 80%, 100% { transform: translate3d(0,0,0); }
  40%, 43% { transform: translate3d(0, -8px, 0); }
  70% { transform: translate3d(0, -4px, 0); }
  90% { transform: translate3d(0, -2px, 0); }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 4px 20px rgba(0,0,0,0.15), 0 0 0px rgba(25, 118, 210, 0.4); }
  50% { box-shadow: 0 6px 30px rgba(0,0,0,0.25), 0 0 20px rgba(25, 118, 210, 0.6); }
`;

// Predefined positions
const positions = {
  'bottom-right': { bottom: 24, right: 24 },
  'bottom-left': { bottom: 24, left: 24 },
  'top-right': { top: 24, right: 24 },
  'top-left': { top: 24, left: 24 },
  'center': { 
    top: '50%', 
    left: '50%', 
    transform: 'translate(-50%, -50%)' 
  },
};

const FloatingActionButton = ({
  // Content options
  text,
  icon,
  variant = 'text', // 'icon' | 'text' | 'extended'
  
  // Positioning
  position = 'bottom-right',
  offset,
  
  // Styling
  size = 'large',
  color = 'primary',
  sx = {},
  
  // Behavior
  onClick,
  tooltip = '',
  placement = 'left',
  animation = 'none', // 'none' | 'pulse' | 'bounce' | 'glow'
  
  // Accessibility
  ariaLabel,
  
  // Responsive
  hideOnMobile = false,
  hideOnDesktop = false,
  
  // Material-UI Fab props
  ...fabProps
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Handle responsive hiding
  if (hideOnMobile && isMobile) return null;
  if (hideOnDesktop && !isMobile) return null;
  
  // Get animation styles
  const getAnimationStyles = () => {
    switch (animation) {
      case 'pulse':
        return { animation: `${pulse} 2s infinite` };
      case 'bounce':
        return { animation: `${bounce} 2s infinite` };
      case 'glow':
        return { animation: `${glow} 2s infinite` };
      default:
        return {};
    }
  };
  
  // Get positioning styles
  const getPositionStyles = () => {
    if (position === 'custom' && offset) {
      return offset;
    }
    return positions[position] || positions['bottom-right'];
  };
  
  // Get responsive size
  const getResponsiveSize = () => {
    if (size === 'auto') {
      return isMobile ? 'medium' : 'large';
    }
    return size;
  };
  
  // Render content based on variant
  const renderContent = () => {
    switch (variant) {
      case 'icon':
        return icon;
      
      case 'text':
        if (typeof text === 'string') {
          // Handle multi-line text (split by spaces or manual line breaks)
          const lines = text.includes('\n') 
            ? text.split('\n') 
            : text.split(' ').length > 1 
              ? text.split(' ') 
              : [text];
          
          return (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center'
            }}>
              {lines.map((line, index) => (
                <Typography 
                  key={index}
                  variant="button" 
                  component="span"
                  sx={{ 
                    fontWeight: 'bold', 
                    lineHeight: 1,
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }}
                >
                  {line}
                </Typography>
              ))}
            </Box>
          );
        }
        return text;
      
      case 'extended':
        return (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1 
          }}>
            {icon}
            <Typography variant="button" sx={{ fontWeight: 'bold' }}>
              {text}
            </Typography>
          </Box>
        );
      
      default:
        return text || icon;
    }
  };
  
  // Get Fab size and styling
  const getFabStyles = () => {
    const baseStyles = {
      position: 'fixed',
      zIndex: 1200,
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'scale(1.05) translateY(-2px)',
        boxShadow: 6,
      },
      ...getPositionStyles(),
      ...getAnimationStyles(),
    };
    
    // Special styling for text variant
    if (variant === 'text') {
      return {
        ...baseStyles,
        width: 80,
        height: 80,
        borderRadius: 2, // Slightly rounded square
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      };
    }
    
    // Special styling for extended variant
    if (variant === 'extended') {
      return {
        ...baseStyles,
        borderRadius: 28, // Oval shape
        px: 2,
        minWidth: 120,
      };
    }
    
    return baseStyles;
  };
  
  const fabElement = (
    <Fab
      color={color}
      size={getResponsiveSize()}
      onClick={onClick}
      aria-label={ariaLabel || tooltip || 'Floating action button'}
      sx={{
        ...getFabStyles(),
        ...sx,
      }}
      {...fabProps}
    >
      {renderContent()}
    </Fab>
  );
  
  // Wrap with tooltip if provided
  if (tooltip) {
    return (
      <Tooltip title={tooltip} placement={placement}>
        <Zoom in={true}>
          {fabElement}
        </Zoom>
      </Tooltip>
    );
  }
  
  return (
    <Zoom in={true}>
      {fabElement}
    </Zoom>
  );
};

export default FloatingActionButton;